/* Prohealth cart rewards strip

   Two small jobs only. All of the progress maths and copy is rendered in
   Liquid, and the drawer re-renders [data-minimog-cart-items] from
   ?section_id=cart-drawer on every cart update, so there is no state to
   keep in sync here.

   One job only: play the stage 2 unlock reveal once per session, rather
   than on every cart update that re-renders the strip.

   There is deliberately no condensed state any more. The strip used to
   shrink once the drawer was scrolled, and because the drawer replaces the
   whole item container on every cart change, the freshly rendered strip
   arrived expanded while the container was still scrolled: the block grew,
   covered the first product, and only snapped back on the next scroll
   event. Two heights for one pinned element cannot be kept in step with a
   container that re-renders underneath it, and the strip is now meant to
   show its numbers at all times anyway, so it has a single fixed layout.

   Safe to run inside the Theme Editor: everything is delegated or
   re-bound by the observer, and re-initialising is idempotent. */

(() => {
  if (window.ProhealthCartRewards) return;

  const MODULE = '[data-ph-rewards]';
  const SEEN_KEY = 'ph-rewards-stage-2-seen';

  /* sessionStorage throws in some privacy modes. Never let that break the
     strip: fall back to an in-memory flag for the life of the page. */
  let memorySeen = false;

  const hasSeenUnlock = () => {
    try {
      return window.sessionStorage.getItem(SEEN_KEY) === 'true';
    } catch (error) {
      return memorySeen;
    }
  };

  const markUnlockSeen = () => {
    memorySeen = true;
    try {
      window.sessionStorage.setItem(SEEN_KEY, 'true');
    } catch (error) {
      /* Nothing to do. The in-memory flag still suppresses replays. */
    }
  };

  const clearUnlockSeen = () => {
    memorySeen = false;
    try {
      window.sessionStorage.removeItem(SEEN_KEY);
    } catch (error) {
      /* Nothing to do. */
    }
  };

  const playUnlock = (module) => {
    if (module.dataset.stage !== '2') {
      /* Dropped back under the threshold, so the reveal is armed again. */
      clearUnlockSeen();
      return;
    }

    if (hasSeenUnlock()) return;

    markUnlockSeen();
    module.classList.add('is-unlocking');
    window.setTimeout(() => module.classList.remove('is-unlocking'), 1100);
  };

  const bindModule = (module) => {
    playUnlock(module);
  };

  const init = (context = document) => {
    context.querySelectorAll(MODULE).forEach(bindModule);
  };

  /* The drawer replaces the inner HTML of its items container wholesale, so
     watch for a replaced strip rather than relying on a cart event. */
  const observe = () => {
    const observer = new MutationObserver((mutations) => {
      const replaced = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(MODULE) || node.querySelector?.(MODULE))
        )
      );
      if (replaced) init();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    init();
    observe();
  };

  window.ProhealthCartRewards = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Theme Editor: sections re-render on every settings change. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
